import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronLeft, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import FlexBetween from "components/FlexBetween";
import { useSelector } from "react-redux";
import { useGetBadgesQuery } from "state/api";
import { isAdmin, isMediaBuyer, isMediaBuyingHead } from "utils/roles";
// Per-role nav structure lives in navConfig.js, shared with BottomNav and the
// Ctrl+K CommandPalette so the three can never drift apart. Items carry icon
// component REFERENCES (Icon), rendered at this component's size below.
import { getSidebarItems } from "../navConfig";
import logoInverted from "../phieg-logo-inverted.png";
import logoNormal from "../phieg-logo.png";

function Sidebar({
  drawerWidth,
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
}) {
  const { pathname } = useLocation(); // gives us out current location
  const [active, setActive] = useState(""); // will be used to keep track of what page we are currently at
  const navigate = useNavigate();
  const theme = useTheme();

  const user = useSelector((state) => state.auth.user);
  const navItems = useMemo(() => getSidebarItems(user), [user]);
  // Media Buying nav badges (inbox unread + system issues) come from ONE
  // badges call — it used to be two polls per open tab, one request in five
  // across the whole CRM. Skipped for roles without the module; the server
  // returns null for any count the caller can't see (a buyer gets no issue
  // count), so there is no separate manager check here. Polling pauses when
  // the tab is hidden.
  const isMbRole = isMediaBuyer(user) || isMediaBuyingHead(user) || isAdmin(user);
  const { data: badges } = useGetBadgesQuery(undefined, {
    skip: !isMbRole,
    pollingInterval: 60000,
    skipPollingIfUnfocused: true,
  });
  const mbUnread = badges?.mbUnread || 0;
  // Badge the FULL unresolved count, like the inbox does. It used to show only
  // `critical`, so a queue of 6 unresolved medium issues showed no badge at all
  // and the tab looked empty. `total` is what "needs attention" means here.
  const mbCritical = badges?.mbIssues?.total ?? badges?.mbIssues?.critical ?? 0;
  // Collapsible Media Buying nav group — remembered in localStorage. Default:
  // expanded for media-buying-centric roles (it's their primary nav), collapsed
  // for admin (whose sidebar also has CRM + Financials, so the MB section's
  // Clients/Partners would otherwise repeat those names).
  // Nav items that own a sub-list (Clients -> "New Client ?"). Keyed by path so
  // the admin nav's repeated labels can't collide.
  const [openItems, setOpenItems] = useState({});
  const toggleItem = (key) =>
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  const [mbNavOpen, setMbNavOpen] = useState(() => {
    try {
      const saved = localStorage.getItem("mb:navOpen");
      if (saved != null) return JSON.parse(saved);
    } catch {}
    return isMediaBuyer(user) || isMediaBuyingHead(user);
  });
  const toggleMbNav = () =>
    setMbNavOpen((v) => {
      const next = !v;
      try {
        localStorage.setItem("mb:navOpen", JSON.stringify(next));
      } catch {}
      return next;
    });

  useEffect(() => {
    const currentPage = pathname.split("/")[1];
    const role = user?.role;
    // Client/Team-Member/Super-Client nav items navigate to unprefixed paths
    // (e.g. "/settings") that redirect to role-prefixed routes
    // (e.g. "/clientsettings"). Strip the prefix so the highlight matches the
    // nav item — but only for those roles, otherwise admin/FM routes that
    // legitimately start with the same prefix (e.g. "/clientsettings",
    // "/clients") get mis-stripped.
    if (role === "Client" && currentPage.startsWith("client")) {
      setActive(currentPage.replace(/^client/, ""));
    } else if (
      role === "Team Member" &&
      currentPage.startsWith("teammember") &&
      currentPage !== "teammembers"
    ) {
      setActive(currentPage.replace(/^teammember/, ""));
    } else if (role === "Super Client" && currentPage.startsWith("superclient")) {
      setActive(currentPage.replace(/^superclient/, ""));
    } else {
      setActive(currentPage);
    }
  }, [pathname, user]);
  const mode = useSelector((state) => state.global.mode);

  const logo = mode === "dark" ? logoNormal : logoInverted;

  // Sidebar sits on a WHITE surface (background.alt = primary[500] = #FFFFFF),
  // so the dark-surface borderSubtle token (rgba white/0.08) is invisible. Use
  // a dark-on-light hairline + text tokens tuned for white instead.
  const lightBorder = "rgba(13, 47, 44, 0.08)";
  const lightBorderStrong = "rgba(13, 47, 44, 0.12)";
  const activeText = "#1a2a27";
  const activeIndicator = theme.palette.extras.accent.main;
  const inactiveText = "#3f4f4c";
  const inactiveIcon = "#6f8782";
  const sectionLabel = "#6f8782";
  const hoverBg = "rgba(127, 176, 154, 0.10)";

  return (
    <Box component="nav">
      <Drawer
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        variant={isNonMobile ? "persistent" : "temporary"}
        anchor="left"
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            display: "flex",
            flexDirection: "column",
            color: inactiveText,
            backgroundColor: theme.palette.background.alt,
            boxSizing: "border-box",
            borderRight: `1px solid ${lightBorder}`,
            width: drawerWidth,
            boxShadow: isNonMobile ? "none" : theme.palette.extras.shadow.md,
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(7, 14, 13, 0.55)",
          },
        }}
      >
        <Box width="100%">
          {/* Logo header — gets a soft on-brand sage halo behind the wordmark
              and a thin gradient divider underneath, so it reads as the
              sidebar's "identity" band rather than just another row. */}
          <Box
            sx={{
              position: "relative",
              px: "1.5rem",
              pt: "1.6rem",
              pb: "1.4rem",
              mb: "0.5rem",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(120% 70% at 20% 0%, rgba(127, 176, 154, 0.16), transparent 70%)",
                pointerEvents: "none",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                left: "1rem",
                right: "1rem",
                bottom: 0,
                height: "1px",
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(13, 47, 44, 0.14) 50%, transparent 100%)",
              },
            }}
          >
            <FlexBetween sx={{ position: "relative" }}>
              <Link
                to="/"
                onClick={() => !isNonMobile && setIsSidebarOpen(false)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <img
                  src={logo}
                  alt="Phieg"
                  style={{ height: "34px", width: "auto" }}
                />
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: activeIndicator,
                    boxShadow: `0 0 0 3px ${theme.palette.extras.accent.subtle}`,
                  }}
                />
              </Link>
              {!isNonMobile && (
                <IconButton
                  onClick={() => setIsSidebarOpen(false)}
                  sx={{ color: inactiveIcon, position: "relative" }}
                >
                  <ChevronLeft />
                </IconButton>
              )}
            </FlexBetween>
          </Box>
          <List sx={{ px: 0, pt: 0 }}>
            {navItems.map(({ text, Icon, path, collapsible, group, children }, idx) => {
              if (!Icon) {
                // Section label — skip the top margin on the very first one so
                // it tucks under the logo divider tightly.
                const isFirstSection =
                  idx === 0 || navItems.slice(0, idx).every((n) => !n.Icon);
                if (collapsible) {
                  return (
                    <ListItemButton
                      key={text}
                      onClick={toggleMbNav}
                      sx={{
                        mx: "1.25rem",
                        mt: isFirstSection ? "0.5rem" : "1.5rem",
                        mb: "0.4rem",
                        px: "0.25rem",
                        py: "0.1rem",
                        minHeight: 0,
                        borderRadius: "6px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        "&:hover": { backgroundColor: hoverBg },
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: 10,
                          letterSpacing: "0.1em",
                          color: sectionLabel,
                          whiteSpace: "nowrap",
                          "&::before": {
                            content: '""',
                            flex: "none",
                            width: 10,
                            height: 1,
                            background: activeIndicator,
                            opacity: 0.75,
                          },
                        }}
                      >
                        {text}
                      </Typography>
                      {mbNavOpen ? (
                        <ExpandLess sx={{ fontSize: 16, color: sectionLabel }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 16, color: sectionLabel }} />
                      )}
                    </ListItemButton>
                  );
                }
                return (
                  <Typography
                    key={text}
                    variant="overline"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      mx: "1.25rem",
                      mt: isFirstSection ? "0.5rem" : "1.5rem",
                      mb: "0.4rem",
                      fontSize: 10,
                      letterSpacing: "0.1em",
                      color: sectionLabel,
                      whiteSpace: "nowrap",
                      "&::before": {
                        content: '""',
                        flex: "none",
                        width: 10,
                        height: 1,
                        background: activeIndicator,
                        opacity: 0.75,
                      },
                    }}
                  >
                    {text}
                  </Typography>
                );
              } else {
                // Hide the Media Buying sub-items when the group is collapsed.
                if (group === "mb" && !mbNavOpen) return null;
                // Allow each nav item to override the URL segment so the
                // displayed label can differ from the route (e.g. "Prefixes"
                // tab points at the existing /projects route).
                const lcText =
                  path || text.toLowerCase().replace(/\s+/g, "");
                // Match the prefix-stripped `active` (Client/Team-Member roles)
                // OR the full pathname, so multi-segment paths like
                // "media-buying/clients" highlight without a single-segment item
                // (e.g. "clients") false-matching "/clientsettings".
                const isActive =
                  active === lcText ||
                  pathname === `/${lcText}` ||
                  pathname.startsWith(`/${lcText}/`);
                const row = (
                  // Keyed by path, NOT label: the admin nav repeats "Clients"
                  // and "Partners" (CRM/Financials + Media Buying group), and
                  // duplicate keys make React mis-reconcile when the list
                  // changes (MB collapse, impersonation role switch), leaving
                  // stale duplicated rows in the sidebar.
                  <ListItem key={lcText} disablePadding sx={{ px: "0.5rem", ...(group === "mb" ? { pl: "1.5rem" } : {}) }}>
                    <ListItemButton
                      onClick={() => {
                        navigate(`/${lcText}`);
                        setActive(lcText);
                        if (!isNonMobile) {
                          setIsSidebarOpen(false);
                        }
                      }}
                      sx={{
                        position: "relative",
                        ml: 0,
                        mr: 0,
                        my: "1px",
                        px: "0.85rem",
                        borderRadius: "10px",
                        color: isActive ? activeText : inactiveText,
                        fontWeight: isActive ? 600 : 500,
                        // Active items use a soft horizontal gradient instead
                        // of a flat tint — gives a subtle "lit" feel.
                        background: isActive
                          ? "linear-gradient(90deg, rgba(127,176,154,0.20) 0%, rgba(127,176,154,0.08) 100%)"
                          : "transparent",
                        transition:
                          "background-color 160ms ease, transform 160ms ease, color 160ms ease",
                        "&::before": isActive
                          ? {
                              content: '""',
                              position: "absolute",
                              left: -8,
                              top: 7,
                              bottom: 7,
                              width: 3,
                              borderRadius: "0 4px 4px 0",
                              backgroundColor: activeIndicator,
                              boxShadow: `0 0 10px ${activeIndicator}, 0 0 18px rgba(127,176,154,0.45)`,
                            }
                          : {},
                        "&:hover": {
                          backgroundColor: isActive
                            ? "transparent"
                            : hoverBg,
                          transform: isActive ? "none" : "translateX(2px)",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: isActive ? activeIndicator : inactiveIcon,
                          minWidth: 34,
                          "& svg": { fontSize: 20 },
                        }}
                      >
                        {path === "media-buying/inbox" && mbUnread > 0 ? (
                          <Badge color="error" badgeContent={mbUnread} max={99}>
                            <Icon />
                          </Badge>
                        ) : path === "media-buying/system-issues" && mbCritical > 0 ? (
                          <Badge color="error" badgeContent={mbCritical} max={99}>
                            <Icon />
                          </Badge>
                        ) : (
                          <Icon />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={text}
                        primaryTypographyProps={{
                          fontSize: 13.5,
                          fontWeight: "inherit",
                          letterSpacing: "0.005em",
                        }}
                      />
                      {children && (
                        // Toggling the sub-list must not also navigate — the
                        // label still opens the parent page on its own.
                        <Box
                          component="span"
                          onClick={(e) => { e.stopPropagation(); toggleItem(lcText); }}
                          sx={{ display: "flex", alignItems: "center", ml: 0.5 }}
                        >
                          {openItems[lcText]
                            ? <ExpandLess sx={{ fontSize: 16 }} />
                            : <ExpandMore sx={{ fontSize: 16 }} />}
                        </Box>
                      )}
                    </ListItemButton>
                  </ListItem>
                );
                if (!children) return row;
                return (
                  <React.Fragment key={lcText}>
                    {row}
                    {openItems[lcText] &&
                      children.map((ch) => {
                        const chActive =
                          pathname === `/${ch.path}` || pathname.startsWith(`/${ch.path}/`);
                        return (
                          <ListItem key={ch.path} disablePadding sx={{ px: "0.5rem", pl: "2.6rem" }}>
                            <ListItemButton
                              onClick={() => {
                                navigate(`/${ch.path}`);
                                setActive(ch.path);
                                if (!isNonMobile) setIsSidebarOpen(false);
                              }}
                              sx={{
                                my: "1px",
                                px: "0.85rem",
                                borderRadius: "10px",
                                color: chActive ? activeText : inactiveText,
                                fontWeight: chActive ? 600 : 500,
                                background: chActive
                                  ? "linear-gradient(90deg, rgba(127,176,154,0.20) 0%, rgba(127,176,154,0.08) 100%)"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: chActive ? "transparent" : hoverBg,
                                },
                              }}
                            >
                              <ListItemText
                                primary={ch.text}
                                primaryTypographyProps={{
                                  fontSize: 12.5,
                                  fontWeight: "inherit",
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                  </React.Fragment>
                );
              }
            })}
          </List>
        </Box>

        {/* Footer — grounds the sidebar with who's signed in + their role,
            and an app version (only when REACT_APP_VERSION is set at build). */}
        <Box
          sx={{
            mt: "auto",
            p: "1rem 1.5rem",
            borderTop: `1px solid ${lightBorderStrong}`,
          }}
        >
          <Typography
            noWrap
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: activeText,
              lineHeight: 1.3,
            }}
          >
            {user?.name || "—"}
          </Typography>
          {user?.role && (
            <Box
              component="span"
              sx={{
                display: "inline-block",
                mt: 0.5,
                px: 0.75,
                py: "1px",
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: "0.02em",
                borderRadius: "999px",
                color: theme.palette.extras.accent.main,
                backgroundColor: theme.palette.extras.accent.subtle,
              }}
            >
              {user.role}
            </Box>
          )}
          {process.env.REACT_APP_VERSION && (
            <Typography
              sx={{
                mt: 0.75,
                fontSize: 10,
                color: sectionLabel,
                opacity: 0.7,
              }}
            >
              v{process.env.REACT_APP_VERSION}
            </Typography>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}

export default Sidebar;
